import { flow, getOr, set, unset } from 'lodash/fp';
import { Reducer } from 'redux';

import { LoginActionTypes } from '../../../auth/login/store/types';
import { ClientDocumentGroup, DocumentNameActionTypes, DocumentNameState, DocumentNameTab, DocumentNamePageView, NewAgreementType, SystemDocumentNameDB, AgreementHierarchyRank, AgreementTypeHierarchy } from './types';

export const newDocumentName: SystemDocumentNameDB = {
    documentName: '',
    datasetId: null,
    datasetTitle: null,
    agreementType: null,
    agencyDatasetId: null,
    agreementTypeId: null,
    boilerPlateEndText: null,
    scheduleStartText: null,
    scheduleEndText: null,
    documentExcludedWords: [],
    documentKeyWords: [],
    potentialDocumentNames: []
};

const newAgreementType: NewAgreementType = {
    agreementTypeId: null,
    name: ''
};

const initialClientDocumentGroup: ClientDocumentGroup = {
    clientDocumentGroupId: null,
    groupName: '',
    description: '',
    documentNameIds: []
};

const initialAgreementHierarchy: AgreementTypeHierarchy = {
    [AgreementHierarchyRank.PARENTS]: [],
    [AgreementHierarchyRank.SELECTED_AGREEMENT]: [],
    [AgreementHierarchyRank.CHILDREN]: [],
    [AgreementHierarchyRank.AVAILABLE_AGREEMENTS]: [],
};

export const INITIAL_STATE: DocumentNameState = {
    modalOpen: false,
    documents: [],
    adminDocumentNames: [],
    currentSystemDocument: null,
    savedSystemDocument: null,
    availableDocumentNames: [],
    clientDatasetConfirmationModalOpen: false,
    savedAgreementTypes: [],
    currentAgreementTypes: [],
    agreementTypeModalOpen: false,
    agreementTypesHaveUpdated: false,
    isUpdatingAgreementTypes: false,
    createDocumentGroupModalOpen: false,
    allDocumentGroups: [],
    isLoadingGroups: false,
    currentClientDocumentGroup: null,
    savedClientDocumentGroup: null,
    isSaving: false,
    mlReadyAgreementTypeIds: [],
    selectedTab: DocumentNameTab.GENERAL,
    documentNameHasUpdated: false,
    unsavedChangesModalOpen: false,
    agreementTypeRelationships: [],
    selectedAgreementTypeId: null,
    currentAgreementHierarchy: initialAgreementHierarchy,
    savedAgreementHierarchy: initialAgreementHierarchy,
    canSaveAgreementHierarchy: false,
    isLoadingAgreementHierarchy: false,
    documentNamePageView: DocumentNamePageView.TABLE,
    generatingReport: false
};

export const documentNameReducer: Reducer<DocumentNameState> = (state = INITIAL_STATE, { payload, type }): DocumentNameState => {
    switch (type) {
        case DocumentNameActionTypes.TOGGLE_DOCUMENT_NAME_MODAL: {
            const documentName = payload.isOpen ? getOr(newDocumentName, 'documentName', payload) : null;
            return flow(
                set('modalOpen', payload.isOpen),
                set('currentSystemDocument', documentName),
                set('savedSystemDocument', documentName),
                set('selectedTab', DocumentNameTab.GENERAL),
                set('unsavedChangesModalOpen', false)
            )(state);
        }
        case DocumentNameActionTypes.UPDATE_DOCUMENT_NAME_VALUE: {
            const { key, value } = payload;
            return set(`currentSystemDocument.${key}`, value, state);
        }
        case DocumentNameActionTypes.ADD_NEW_DOCUMENT_NAME_AI_DISCOVER_ITEM: {
            const key = payload as 'potentialDocumentNames' | 'documentKeyWords' | 'documentExcludedWords';
            const currentItems = state.currentSystemDocument![key] || [];
            const updatedItems = [...currentItems, ''];
            return set(`currentSystemDocument.${key}`, updatedItems, state);
        }
        case DocumentNameActionTypes.UPDATE_DOCUMENT_NAME_AI_DISCOVER_VALUE: {
            const { key, index, value } = payload;
            return set(`currentSystemDocument.${key}[${index}]`, value, state);
        }
        case DocumentNameActionTypes.DELETE_DOCUMENT_NAME_AI_DISCOVER_ITEM: {
            const { key, indexToDelete } = payload;
            const aiDiscoverKey = key as 'potentialDocumentNames' | 'documentKeyWords' | 'documentExcludedWords';
            const currentItems = state.currentSystemDocument![aiDiscoverKey];
            if (currentItems && currentItems.length > 0) {
                const updatedItems = currentItems.filter((_, index) => index !== indexToDelete);
                return set(`currentSystemDocument.${key}`, updatedItems, state);
            }
            return state;
        }
        case DocumentNameActionTypes.UPDATE_DOCUMENT_NAME_SUCCESSFUL:
            return flow(
                set('modalOpen', false),
                set('adminDocumentNames', payload),
                set('clientDatasetConfirmationModalOpen', false),
                set('selectedTab', DocumentNameTab.GENERAL)
            )(state);
        case DocumentNameActionTypes.FETCH_ALL_DOCUMENT_NAMES_SUCCESSFUL:
            return set('documents', payload, state);
        case DocumentNameActionTypes.FETCH_ALL_ADMIN_DOCUMENT_NAMES_SUCCESSFUL:
            return set('adminDocumentNames', payload, state);
        case DocumentNameActionTypes.FETCH_ALL_AGREEMENT_TYPES_SUCCESSFUL:
            return flow(
                set('savedAgreementTypes', payload),
                set('currentAgreementTypes', payload)
            )(state);
        case DocumentNameActionTypes.FETCH_AVAILABLE_DOCUMENT_NAMES_SUCCESSFUL:
            return set('availableDocumentNames', payload, state);
        case DocumentNameActionTypes.TOGGLE_CLIENT_DATASET_CONFIRMATION_MODAL_OPEN:
            if (payload) {
                return set('clientDatasetConfirmationModalOpen', payload, state);
            }
            return flow(
                unset('currentSystemDocument.clientIds'),
                set('clientDatasetConfirmationModalOpen', payload)
            )(state);
        case DocumentNameActionTypes.FETCH_ALL_DOCUMENT_GROUPS_STARTED:
            return set('isLoadingGroups', true, state);
        case DocumentNameActionTypes.FETCH_ALL_DOCUMENT_GROUPS_SUCCESSFUL:
            return flow(
                set('isLoadingGroups', false),
                set('allDocumentGroups', payload)
            )(state);
        case DocumentNameActionTypes.FETCH_ALL_DOCUMENT_GROUPS_FAILED:
            return set('isLoadingGroups', false, state);
        case DocumentNameActionTypes.TOGGLE_CREATE_DOCUMENT_GROUP_MODAL_OPEN: {
            const { isOpen, clientDocumentGroupId } = payload;
            const currentClientDocumentGroup = state.allDocumentGroups.find((group) => group.clientDocumentGroupId === clientDocumentGroupId) || initialClientDocumentGroup;
            return flow(
                set('currentClientDocumentGroup', currentClientDocumentGroup),
                set('savedClientDocumentGroup', currentClientDocumentGroup),
                set('createDocumentGroupModalOpen', isOpen)
            )(state);
        }
        case DocumentNameActionTypes.UPDATE_CLIENT_DOCUMENT_GROUP: {
            const { key, value } = payload;
            return set(`currentClientDocumentGroup[${key}]`, value, state);
        }
        case DocumentNameActionTypes.SAVE_CLIENT_DOCUMENT_GROUP_STARTED:
            return set('isSaving', true, state);
        case DocumentNameActionTypes.SAVE_CLIENT_DOCUMENT_GROUP_SUCCESSFUL:
            return flow(
                set('isSaving', false),
                set('createDocumentGroupModalOpen', false),
                set('savedClientDocumentGroup', null),
                set('currentClientDocumentGroup', null)
            )(state);
        case DocumentNameActionTypes.SAVE_CLIENT_DOCUMENT_GROUP_FAILED:
            return set('isSaving', false, state);
        case DocumentNameActionTypes.DELETE_CLIENT_DOCUMENT_GROUP_SUCCESSFUL:
            return flow(
                set('createDocumentGroupModalOpen', false),
                set('savedClientDocumentGroup', null),
                set('currentClientDocumentGroup', null)
            )(state);
        case DocumentNameActionTypes.TOGGLE_AGREEMENT_TYPE_MODAL_OPEN:
            return flow(
                set('agreementTypeModalOpen', payload),
                set('currentAgreementTypes', state.savedAgreementTypes),
                set('agreementTypesHaveUpdated', false)
            )(state);
        case DocumentNameActionTypes.ADD_NEW_AGREEMENT_TYPE:
            return set('currentAgreementTypes', [...state.currentAgreementTypes, newAgreementType], state);
        case DocumentNameActionTypes.REMOVE_NEW_AGREEMENT_TYPE:
            return set('currentAgreementTypes', state.currentAgreementTypes.filter((_, index) => index !== payload), state);
        case DocumentNameActionTypes.UPDATE_AGREEMENT_TYPE_NAME: {
            const { index, name } = payload;
            const currentAgreementTypes = state.currentAgreementTypes.map((agreementType, i) => index === i ? set('name', name, agreementType) : agreementType);
            return set('currentAgreementTypes', currentAgreementTypes, state);
        }
        case DocumentNameActionTypes.SET_AGREEMENT_TYPES_HAVE_UPDATED:
            return set('agreementTypesHaveUpdated', payload, state);
        case DocumentNameActionTypes.UPDATE_AGREEMENT_TYPES_STARTED:
            return set('isUpdatingAgreementTypes', true, state);
        case DocumentNameActionTypes.UPDATE_AGREEMENT_TYPES_SUCCESSFUL:
            return flow(
                set('isUpdatingAgreementTypes', false),
                set('currentAgreementTypes', payload),
                set('savedAgreementTypes', payload),
                set('agreementTypesHaveUpdated', false)
            )(state);
        case DocumentNameActionTypes.UPDATE_AGREEMENT_TYPES_FAILED:
            return set('isUpdatingAgreementTypes', false, state);
        case DocumentNameActionTypes.FETCH_ML_READY_AGREEMENT_TYPES_SUCCESSFUL:
            return set('mlReadyAgreementTypeIds', payload, state);
        case DocumentNameActionTypes.SET_DOCUMENT_NAME_WIZARD_TAB:
            return set('selectedTab', payload, state);
        case DocumentNameActionTypes.SET_SYSTEM_DOCUMENT_NAME_HAS_UPDATED:
            return set('documentNameHasUpdated', payload, state);
        case DocumentNameActionTypes.TOGGLE_UNSAVED_DOCUMENT_NAME_MODAL_OPEN:
            return set('unsavedChangesModalOpen', payload, state);
        case DocumentNameActionTypes.FETCH_ALL_AGREEMENT_TYPE_RELATIONSHIPS_SUCCESSFUL:
            return set('agreementTypeRelationships', payload, state);
        case DocumentNameActionTypes.SET_AGREEMENT_RELATIONSHIP_ID:
            return set('selectedAgreementTypeId', payload, state);
        case DocumentNameActionTypes.RESET_AGREEMENT_HIERARCHY:
            return flow(
                set('currentAgreementHierarchy', initialAgreementHierarchy),
                set('savedAgreementHierarchy', initialAgreementHierarchy),
                set('canSaveAgreementHierarchy', false)
            )(state);
        case DocumentNameActionTypes.UPDATE_AGREEMENT_HIERARCHY:
            return set('currentAgreementHierarchy', payload, state);
        case DocumentNameActionTypes.SAVE_AGREEMENT_TYPE_HIERARCHY_SUCCESSFUL: {
            const { agreementTypeRelationships, agreementTypeHierarchy } = payload;
            return flow(
                set('agreementTypeRelationships', agreementTypeRelationships),
                set('currentAgreementHierarchy', agreementTypeHierarchy),
                set('savedAgreementHierarchy', agreementTypeHierarchy),
                set('isLoadingAgreementHierarchy', false)
            )(state);
        }
        case DocumentNameActionTypes.SET_CAN_SAVE_AGREEMENT_HIERARCHY:
            return set('canSaveAgreementHierarchy', payload, state);
        case DocumentNameActionTypes.SAVE_AGREEMENT_TYPE_HIERARCHY_STARTED:
        case DocumentNameActionTypes.FETCH_AGREEMENT_TYPE_HIERARCHY_STARTED:
            return set('isLoadingAgreementHierarchy', true, state);
        case DocumentNameActionTypes.FETCH_AGREEMENT_TYPE_HIERARCHY_SUCCESSFUL:
            return flow(
                set('currentAgreementHierarchy', payload),
                set('savedAgreementHierarchy', payload),
                set('isLoadingAgreementHierarchy', false)
            )(state);
        case DocumentNameActionTypes.SAVE_AGREEMENT_TYPE_HIERARCHY_FAILED:
        case DocumentNameActionTypes.FETCH_AGREEMENT_TYPE_HIERARCHY_FAILED:
            return set('isLoadingAgreementHierarchy', false, state);
        case DocumentNameActionTypes.SET_DOCUMENT_NAME_PAGE_VIEW:
            return set('documentNamePageView', payload, state);
        case DocumentNameActionTypes.GENERATE_REPORT_STARTED:
            return set('generatingReport', true, state);
        case DocumentNameActionTypes.GENERATE_REPORT_SUCCESSFUL:
        case DocumentNameActionTypes.GENERATE_REPORT_FAILED:
            return set('generatingReport', false, state);
        case LoginActionTypes.LOGOUT_SUCCESSFUL:
            return INITIAL_STATE;
        default:
            return state;
    }
};
